import React, { useEffect, useState } from "react";
import LivePriceBadge from "../../components/LivePriceBadge";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { fmtDate, isoDateInput, num, inr, pct } from "../../lib/format";

export default function RAPanel({ tab = "recommendations" }) {
  const [activeTab, setActiveTab] = useState(tab);
  React.useEffect(() => { setActiveTab(tab); }, [tab]);
  const [recs, setRecs] = useState([]);
  const [positions, setPositions] = useState([]);
  const [bhav, setBhav] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [stratForm, setStratForm] = useState({ name: "", description: "", max_portfolio_size: 50 });
  const [stratBusy, setStratBusy] = useState(false);
  const [recForm, setRecForm] = useState({ strategy_id: "", company: "", symbol: "", industry: "", close_price: "", effective_date: isoDateInput() });
  const [recBulkFile, setRecBulkFile] = useState(null);
  const [recBulkStrategy, setRecBulkStrategy] = useState("");
  const [recBulkResult, setRecBulkResult] = useState(null);
  const [recBulkBusy, setRecBulkBusy] = useState(false);
  const [bhavForm, setBhavForm] = useState({ trade_date: isoDateInput(), nifty50_close: "", nifty_smallcap50_close: "", file: null });
  const [idxBulkFile, setIdxBulkFile] = useState(null);
  const [idxBulkResult, setIdxBulkResult] = useState(null);
  const [idxBulkBusy, setIdxBulkBusy] = useState(false);
  const [nseForm, setNseForm] = useState({
    from_date: isoDateInput(new Date(Date.now() - 30 * 86400000)),
    to_date: isoDateInput(),
  });
  const [nseResult, setNseResult] = useState(null);
  const [nseBusy, setNseBusy] = useState(false);
  const [engineResult, setEngineResult] = useState(null);
  const [engineDetailsOpen, setEngineDetailsOpen] = useState(false);
  const [backForm, setBackForm] = useState({
    from_date: isoDateInput(new Date(Date.now() - 30 * 86400000)),
    to_date: isoDateInput(),
    reset: true,
  });
  const [backResult, setBackResult] = useState(null);
  const [backBusy, setBackBusy] = useState(false);
  const [snapBackfillBusy, setSnapBackfillBusy] = useState(false);
  const [snapBackfillResult, setSnapBackfillResult] = useState(null);

  const load = () => {
    api.get("/ra/recommendations").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/ra/positions").then((r) => setPositions(r.data)).catch(() => {});
    api.get("/ra/bhavcopy").then((r) => setBhav(r.data)).catch(() => {});
    api.get("/strategies-with-stats").then((r) => setStrategies(r.data)).catch(() => {
      api.get("/strategies", { params: { include_inactive: true } }).then((r) => setStrategies(r.data)).catch(() => {});
    });
  };
  useEffect(load, []);

  const activeStrategies = strategies.filter((s) => s.is_active);

  const submitStrategy = async (e) => {
    e.preventDefault();
    if (!stratForm.name.trim()) { toast.error("Name required"); return; }
    setStratBusy(true);
    try {
      await api.post("/ra/strategies", stratForm);
      toast.success(`Strategy '${stratForm.name}' created`);
      setStratForm({ name: "", description: "", max_portfolio_size: 50 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setStratBusy(false);
    }
  };

  const toggleStrategy = async (s) => {
    try {
      await api.patch(`/ra/strategies/${s.strategy_id}`, { is_active: !s.is_active });
      toast.success(`'${s.name}' ${s.is_active ? "deactivated" : "reactivated"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitRec = async (e) => {
    e.preventDefault();
    if (!recForm.strategy_id) { toast.error("Pick a strategy"); return; }
    try {
      const payload = {
        strategy_id: recForm.strategy_id,
        action: "buy",
        company: recForm.company,
        symbol: recForm.symbol,
        industry: recForm.industry,
        close_price: Number(recForm.close_price),
        effective_date: recForm.effective_date || undefined,
      };
      await api.post("/ra/recommendations", payload);
      toast.success(`BUY ${recForm.symbol} added to '${activeStrategies.find(s => s.strategy_id === recForm.strategy_id)?.name}' · engine firing in background`);
      setRecForm({ strategy_id: recForm.strategy_id, company: "", symbol: "", industry: "", close_price: "", effective_date: isoDateInput() });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitSell = async (pos) => {
    const price = window.prompt(`Sell rate for ${pos.symbol} in '${pos.strategy_name}' (₹):`, pos.current_rate ? pos.current_rate.toFixed(2) : "");
    if (price === null) return;
    const close_price = Number(price);
    if (!close_price || isNaN(close_price)) { toast.error("Invalid price"); return; }
    try {
      await api.post("/ra/recommendations", {
        strategy_id: pos.strategy_id,
        action: "sell",
        company: pos.company || pos.symbol,
        symbol: pos.symbol,
        industry: pos.industry || "—",
        close_price,
        effective_date: isoDateInput(),
      });
      toast.success(`SELL ${pos.symbol} recorded in '${pos.strategy_name}' at ₹${close_price.toFixed(2)} · engine firing in background`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitPaperClose = async (pos) => {
    const prefill = pos.current_rate ? pos.current_rate.toFixed(2) : "";
    const price = window.prompt(`Paper-close ${pos.symbol} in '${pos.strategy_name}' — Sell rate (₹):\n\nThis pending BUY was never allotted to any client portfolio. Closing it will record the trade in the strategy P&L only.`, prefill);
    if (price === null) return;
    const sell_price = Number(price);
    if (!sell_price || isNaN(sell_price)) { toast.error("Invalid price"); return; }
    try {
      await api.post(`/ra/recommendations/${pos.rec_id}/paper-close`, { sell_price });
      toast.success(`Paper-closed ${pos.symbol} @ ₹${sell_price.toFixed(2)} — strategy P&L updated`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error || "Failed to paper-close"));
    }
  };

  const uploadRecBulk = async (e) => {
    e.preventDefault();
    if (!recBulkFile) { toast.error("Select a CSV"); return; }
    if (!recBulkStrategy) { toast.error("Pick a strategy"); return; }
    const fd = new FormData();
    fd.append("file", recBulkFile);
    fd.append("strategy_id", recBulkStrategy);
    setRecBulkBusy(true);
    setRecBulkResult(null);
    try {
      const r = await api.post("/ra/recommendations/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRecBulkResult(r.data);
      toast.success(`${r.data.rows_inserted} recommendations uploaded · engine firing in background`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setRecBulkBusy(false);
    }
  };

  const uploadIndices = async (e) => {
    e.preventDefault();
    if (!idxBulkFile) { toast.error("Select a CSV"); return; }
    const fd = new FormData();
    fd.append("file", idxBulkFile);
    setIdxBulkBusy(true);
    setIdxBulkResult(null);
    try {
      const r = await api.post("/ra/indices/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdxBulkResult(r.data);
      toast.success(`${r.data.rows_inserted} index rows uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setIdxBulkBusy(false);
    }
  };

  const [backProgress, setBackProgress] = useState(null);

  const runBacktest = async () => {
    setBackBusy(true);
    setBackResult(null);
    setBackProgress({ status: "queued", days_done: 0, days_total: 0 });
    try {
      const r = await api.post("/ra/engine/backtest", backForm);
      const jobId = r.data.job_id;
      // Poll the job until done
      let poll = 0;
      const tick = async () => {
        poll += 1;
        try {
          const j = await api.get(`/ra/engine/backtest/${jobId}`);
          const job = j.data;
          setBackProgress({ status: job.status, days_done: job.progress?.days_done || 0, days_total: job.progress?.days_total || 0, current_day: job.progress?.current_day });
          if (job.status === "done") {
            setBackResult(job.result);
            toast.success(`Backtest done: ${job.result.trade_days} days, ${job.result.total_buys} buys, ${job.result.total_sells} sells`);
            setBackBusy(false);
            setBackProgress(null);
            load();
            return;
          }
          if (job.status === "failed") {
            toast.error(`Backtest failed: ${job.error || "Unknown error"}`);
            setBackBusy(false);
            setBackProgress(null);
            return;
          }
          // Still running — poll again in 2s; give up after 10 min
          if (poll > 300) {
            toast.error("Backtest timed out (>10 min). Check server logs.");
            setBackBusy(false);
            setBackProgress(null);
            return;
          }
          setTimeout(tick, 2000);
        } catch (e) {
          toast.error("Lost connection to backtest job — retrying...");
          setTimeout(tick, 3000);
        }
      };
      setTimeout(tick, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backtest failed to start");
      setBackBusy(false);
      setBackProgress(null);
    }
  };

  const uploadBhav = async (e) => {
    e.preventDefault();
    if (!bhavForm.file) { toast.error("Select a CSV"); return; }
    const fd = new FormData();
    fd.append("trade_date", bhavForm.trade_date);
    fd.append("nifty50_close", bhavForm.nifty50_close);
    fd.append("nifty_smallcap50_close", bhavForm.nifty_smallcap50_close);
    fd.append("file", bhavForm.file);
    try {
      const r = await api.post("/ra/bhavcopy/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${r.data.rows_count} symbols`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const fetchNSE = async () => {
    setNseBusy(true);
    setNseResult(null);
    try {
      const r = await api.post("/ra/bhavcopy/nse-fetch", nseForm);
      setNseResult(r.data);
      if (r.data.days_fetched > 0) {
        toast.success(`NSE fetch: ${r.data.days_fetched} days fetched, ${r.data.days_failed} failed`);
      } else {
        toast.error(`NSE fetch returned 0 days. ${r.data.days_failed} attempts failed — try a smaller range or use bulk CSV.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "NSE fetch failed");
    } finally {
      setNseBusy(false);
    }
  };

  const backfillSnapshots = async () => {
    setSnapBackfillBusy(true);
    setSnapBackfillResult(null);
    try {
      const r = await api.post("/admin/snapshots/backfill");
      setSnapBackfillResult(r.data);
      toast.success(`Backfilled ${r.data.snapshots_written} snapshots across ${r.data.days_processed} days`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setSnapBackfillBusy(false);
    }
  };

  const runEngine = async () => {
    try {
      const r = await api.post("/ra/engine/run");
      setEngineResult(r.data);
      setEngineDetailsOpen(false);
      const base = `Engine: ${r.data.buys} buys, ${r.data.sells} sells across ${r.data.portfolios_processed} portfolios`;
      const remarks = r.data.remarks || [];
      if (remarks.length) {
        toast.warning(base, {
          description: remarks[0] + (remarks.length > 1 ? ` (+${remarks.length - 1} more — expand details below)` : ""),
          duration: Infinity,
          cancel: { label: "Dismiss", onClick: () => {} },
        });
      } else if (r.data.skipped > 0) {
        toast.success(base, {
          description: `${r.data.skipped} rec×portfolio pair(s) skipped — expand details below for reasons.`,
          duration: Infinity,
          cancel: { label: "Dismiss", onClick: () => {} },
        });
      } else {
        toast.success(base);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Research Desk</div>
        <h1 className="font-display font-bold text-3xl text-zinc-950 mt-1">RA Console</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="strategies">Strategies</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          <TabsTrigger value="bhavcopy">Bhavcopy</TabsTrigger>
          <TabsTrigger value="engine">Run Engine</TabsTrigger>
          <TabsTrigger value="backtest">Backtest</TabsTrigger>
        </TabsList>

        <TabsContent value="strategies" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-zinc-950">Create new strategy</h3>
                  <p className="text-xs text-zinc-500 mt-1">A strategy is a portfolio type (e.g., "Large Cap", "Mid Cap"). Recommendations and client portfolios are scoped to a strategy.</p>
                </div>
              </div>
              <form onSubmit={submitStrategy} className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-2">
                  <Label>Name</Label>
                  <Input data-testid="strat-name-input" required placeholder="e.g., Mid Cap" value={stratForm.name} onChange={(e) => setStratForm({ ...stratForm, name: e.target.value })} className="mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Input data-testid="strat-desc-input" placeholder="Short note (optional)" value={stratForm.description} onChange={(e) => setStratForm({ ...stratForm, description: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Max Portfolio Size</Label>
                  <Input data-testid="strat-max-size-input" type="number" min="1" max="1000" required value={stratForm.max_portfolio_size} onChange={(e) => setStratForm({ ...stratForm, max_portfolio_size: Number(e.target.value) })} className="mt-1 font-mono" />
                </div>
                <div className="flex items-end">
                  <Button data-testid="add-strat-btn" type="submit" disabled={stratBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white w-full">
                    {stratBusy ? "Creating..." : "Create"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <span className="font-display font-semibold text-zinc-950">All strategies</span>
                  <span className="ml-2 text-xs text-zinc-500">PnL measured at ₹10,000 notional per BUY rec (since-inception, independent of client portfolios).</span>
                </div>
                <Badge className="bg-zinc-50 text-zinc-700 border-zinc-200">{strategies.length}</Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Live</TableHead>
                    <TableHead className="text-right">PnL (₹)</TableHead>
                    <TableHead className="text-right">% PnL</TableHead>
                    <TableHead className="text-right">Exited</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                    <TableHead className="text-right w-32">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {strategies.length === 0 && <TableRow><TableCell colSpan={9} className="text-zinc-500">No strategies yet.</TableCell></TableRow>}
                  {strategies.map((s) => {
                    const st = s.stats || { live_stocks: 0, exited_stocks: 0, pnl: 0, pnl_pct: 0 };
                    const pnlClass = st.pnl > 0 ? "text-profit" : st.pnl < 0 ? "text-loss" : "text-zinc-700";
                    return (
                    <TableRow key={s.strategy_id} data-testid={`strat-row-${s.strategy_id}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => window.location.assign(`/ra/strategies/${s.strategy_id}`)}>
                      <TableCell className="font-display font-semibold text-zinc-900">{s.name}</TableCell>
                      <TableCell className="text-right font-mono">{st.live_stocks}</TableCell>
                      <TableCell className={`text-right font-mono ${pnlClass}`}>{inr(st.pnl, { decimals: 0 })}</TableCell>
                      <TableCell className={`text-right font-mono ${pnlClass}`}>{st.pnl_pct.toFixed(2)}%</TableCell>
                      <TableCell className="text-right font-mono text-zinc-500">{st.exited_stocks}</TableCell>
                      <TableCell className="text-sm text-zinc-600 max-w-xs truncate" title={s.description}>{s.description || "—"}</TableCell>
                      <TableCell>
                        <Badge className={s.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}>
                          {s.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs text-zinc-500 font-mono">{fmtDate(s.created_at)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {s.strategy_id === "strat_default" ? (
                          <span className="text-zinc-400 text-xs">system</span>
                        ) : (
                          <Button size="sm" variant="outline" data-testid={`toggle-strat-${s.strategy_id}`} onClick={() => toggleStrategy(s)} className="h-7 text-xs">
                            {s.is_active ? "Deactivate" : "Reactivate"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-zinc-950">New Recommendation</h3>
                  <p className="text-xs text-zinc-500 mt-1">Recommend a new BUY for a stock that's not already actively held. Use the Sell button on each held position below to exit.</p>
                </div>
                <Badge className="bg-green-50 text-green-700 border-green-200">BUY only</Badge>
              </div>
              <form onSubmit={submitRec} className="grid grid-cols-1 md:grid-cols-7 gap-4">
                <div className="md:col-span-1">
                  <Label>Strategy</Label>
                  <Select value={recForm.strategy_id} onValueChange={(v) => setRecForm({ ...recForm, strategy_id: v })}>
                    <SelectTrigger data-testid="rec-strategy-select" className="mt-1"><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent>
                      {activeStrategies.map((s) => (
                        <SelectItem key={s.strategy_id} value={s.strategy_id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Company</Label><Input data-testid="rec-company-input" required placeholder="e.g., Reliance Industries" value={recForm.company} onChange={(e) => setRecForm({...recForm, company: e.target.value})} className="mt-1" /></div>
                <div>
                  <Label>Symbol</Label>
                  <Input data-testid="rec-symbol-input" required placeholder="e.g., RELIANCE" value={recForm.symbol} onChange={(e) => setRecForm({...recForm, symbol: e.target.value.toUpperCase()})} className="mt-1 font-mono" />
                  <LivePriceBadge symbol={recForm.symbol} />
                </div>
                <div><Label>Industry</Label><Input data-testid="rec-industry-input" required placeholder="e.g., Energy" value={recForm.industry} onChange={(e) => setRecForm({...recForm, industry: e.target.value})} className="mt-1" /></div>
                <div><Label>Buy rate (₹)</Label><Input data-testid="rec-rate-input" required type="number" step="0.01" placeholder="e.g., 2920.50" value={recForm.close_price} onChange={(e) => setRecForm({...recForm, close_price: e.target.value})} className="mt-1 font-mono" /></div>
                <div><Label>Effective from</Label><Input data-testid="rec-effective-input" required type="date" value={recForm.effective_date} onChange={(e) => setRecForm({...recForm, effective_date: e.target.value})} className="mt-1" /></div>
                <div className="flex items-end">
                  <Button data-testid="add-rec-btn" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white w-full">Add</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-zinc-950">Bulk upload recommendations (historical)</h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    CSV columns:{" "}
                    <span className="font-mono">trade_date, close, action, company, symbol, industry</span>.
                    Each row creates one rec with embedded buy/sell rate and effective date.
                  </p>
                </div>
                <Badge className="bg-blue-50 text-blue-700 border-blue-200">For backtesting</Badge>
              </div>
              <form onSubmit={uploadRecBulk} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <Label>Strategy</Label>
                  <Select value={recBulkStrategy} onValueChange={setRecBulkStrategy}>
                    <SelectTrigger data-testid="rec-bulk-strategy" className="mt-1"><SelectValue placeholder="Pick…" /></SelectTrigger>
                    <SelectContent>
                      {activeStrategies.map((s) => (
                        <SelectItem key={s.strategy_id} value={s.strategy_id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>CSV file</Label>
                  <Input data-testid="rec-bulk-file" type="file" accept=".csv" required onChange={(e) => setRecBulkFile(e.target.files[0])} className="mt-1" />
                </div>
                <Button data-testid="rec-bulk-btn" type="submit" disabled={recBulkBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  {recBulkBusy ? "Uploading..." : "Bulk Upload"}
                </Button>
              </form>
              {recBulkResult && (
                <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-md p-4 font-mono text-xs space-y-1">
                  <div>Rows inserted: <span className="text-zinc-900 font-semibold">{recBulkResult.rows_inserted}</span></div>
                  {recBulkResult.issues?.length > 0 && (
                    <div className="text-amber-700 mt-2">
                      Issues ({recBulkResult.issues.length}): {recBulkResult.issues.slice(0, 3).join(" · ")}
                      {recBulkResult.issues.length > 3 && " …"}
                    </div>
                  )}
                </div>
              )}
              <details className="mt-4 text-xs text-zinc-600">
                <summary className="cursor-pointer text-zinc-700 font-semibold">Sample CSV format</summary>
                <pre className="mt-2 p-3 bg-zinc-50 border border-zinc-200 rounded overflow-x-auto text-[11px] leading-snug">{`trade_date,close,action,company,symbol,industry
2023-01-10,2015,buy,Reliance Industries,RELIANCE,Energy
2023-01-10,2501,buy,Tata Consultancy Services,TCS,IT
2023-01-15,1685,buy,HDFC Bank,HDFCBANK,Financials
2023-04-20,2210,sell,Reliance Industries,RELIANCE,Energy
2023-06-12,2350,buy,Reliance Industries,RELIANCE,Energy
2023-08-05,1425,buy,Larsen & Toubro,LT,Industrials`}</pre>
              </details>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div className="font-display font-semibold text-zinc-950">Recommendations — Position Lifecycle</div>
                <Badge className="bg-zinc-50 text-zinc-700 border-zinc-200">{positions.length}</Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right w-12">No.</TableHead>
                    <TableHead>Strategy</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead className="text-right">Buy Date</TableHead>
                    <TableHead className="text-right">Buy Rate</TableHead>
                    <TableHead>Buy Status</TableHead>
                    <TableHead className="text-right">Current / Sell Date</TableHead>
                    <TableHead className="text-right">Current / Sell Rate</TableHead>
                    <TableHead>Sell Status</TableHead>
                    <TableHead className="text-right">% P&L</TableHead>
                    <TableHead className="text-right w-16">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.length === 0 && <TableRow><TableCell colSpan={12} className="text-zinc-500">No recommendations yet. Add a BUY above or bulk-upload historical recs.</TableCell></TableRow>}
                  {positions.map((p, idx) => {
                    const held = p.sell_date === null && p.buy_date !== null;
                    const isPending = p.buy_status === "Pending" && !p.sell_date;
                    const pnlNum = p.pnl_pct;
                    const pnlClass = pnlNum === null ? "text-zinc-500" : pnlNum > 0 ? "text-profit" : pnlNum < 0 ? "text-loss" : "text-zinc-700";
                    const buyBadge = p.buy_status === "Processed" ? "bg-zinc-100 text-zinc-700 border-zinc-200" : p.buy_status === "Pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-zinc-50 text-zinc-500 border-zinc-200";
                    const sellBadge = p.sell_status === "Processed" ? "bg-zinc-100 text-zinc-700 border-zinc-200" : p.sell_status === "Held" ? "bg-green-50 text-green-700 border-green-200" : p.sell_status?.startsWith("Pending") ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-zinc-50 text-zinc-500 border-zinc-200";
                    return (
                      <TableRow key={p.rec_id} data-testid={`pos-${p.symbol}-${idx}`} className={isPending ? "bg-amber-50/50" : ""}>
                        <TableCell className="text-right font-mono text-zinc-500 text-xs">{idx + 1}</TableCell>
                        <TableCell className="text-xs">
                          <Badge className="bg-purple-50 text-purple-700 border-purple-200">{p.strategy_name || "—"}</Badge>
                        </TableCell>
                        <TableCell className="font-mono font-semibold" title={p.company}>{p.symbol}</TableCell>
                        <TableCell className="text-sm text-zinc-600">{p.industry}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{p.buy_date ? fmtDate(p.buy_date) : "—"}</TableCell>
                        <TableCell className="text-right font-mono">{p.buy_rate ? inr(p.buy_rate, { decimals: 2 }) : "—"}</TableCell>
                        <TableCell><Badge className={buyBadge}>{p.buy_status}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-sm">{p.sell_date ? fmtDate(p.sell_date) : (p.current_date ? fmtDate(p.current_date) : "—")}</TableCell>
                        <TableCell className="text-right font-mono">{p.sell_rate ? inr(p.sell_rate, { decimals: 2 }) : (p.current_rate ? inr(p.current_rate, { decimals: 2 }) : "—")}</TableCell>
                        <TableCell><Badge className={sellBadge}>{p.sell_status}</Badge></TableCell>
                        <TableCell className={`text-right font-mono ${pnlClass}`}>{pnlNum === null ? "—" : pct(pnlNum)}</TableCell>
                        <TableCell className="text-right">
                          {isPending ? (
                            <Button size="sm" variant="outline" data-testid={`sell-btn-${p.symbol}`} onClick={() => submitPaperClose(p)} className="h-7 text-xs">Sell</Button>
                          ) : held ? (
                            <Button size="sm" variant="outline" data-testid={`sell-btn-${p.symbol}`} onClick={() => submitSell(p)} className="h-7 text-xs">Sell</Button>
                          ) : (
                            <span className="text-zinc-400 text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bhavcopy" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <h3 className="font-display font-semibold text-zinc-950 mb-4">Upload Bhavcopy CSV (single day)</h3>
              <p className="text-xs text-zinc-500 mb-4">Required columns: <span className="font-mono">SYMBOL, CLOSE</span> (extra NSE columns are tolerated).</p>
              <form onSubmit={uploadBhav} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Trade date</Label><Input type="date" required value={bhavForm.trade_date} onChange={(e) => setBhavForm({...bhavForm, trade_date: e.target.value})} className="mt-1" /></div>
                <div><Label>CSV file</Label><Input data-testid="bhav-file-input" type="file" accept=".csv" required onChange={(e) => setBhavForm({...bhavForm, file: e.target.files[0]})} className="mt-1" /></div>
                <div><Label>Nifty 50 close</Label><Input type="number" step="0.01" required value={bhavForm.nifty50_close} onChange={(e) => setBhavForm({...bhavForm, nifty50_close: e.target.value})} className="mt-1 font-mono" /></div>
                <div><Label>Nifty Smallcap 50 close</Label><Input type="number" step="0.01" required value={bhavForm.nifty_smallcap50_close} onChange={(e) => setBhavForm({...bhavForm, nifty_smallcap50_close: e.target.value})} className="mt-1 font-mono" /></div>
                <div className="md:col-span-2"><Button data-testid="upload-bhav-btn" type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white">Upload</Button></div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-zinc-950">Auto-fetch from NSE archives</h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    Downloads daily bhavcopy ZIPs + Nifty 50 + Nifty Smallcap 50 history straight from <span className="font-mono">nseindia.com</span>.
                    Best-effort — NSE may rate-limit scripted requests. If many days fail, fall back to bulk CSV upload.
                  </p>
                </div>
                <Badge className="bg-amber-50 text-amber-700 border-amber-200">Beta</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <Label>From date</Label>
                  <Input data-testid="nse-from-input" type="date" required value={nseForm.from_date} onChange={(e) => setNseForm({...nseForm, from_date: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label>To date</Label>
                  <Input data-testid="nse-to-input" type="date" required value={nseForm.to_date} onChange={(e) => setNseForm({...nseForm, to_date: e.target.value})} className="mt-1" />
                </div>
                <Button data-testid="nse-fetch-btn" onClick={fetchNSE} disabled={nseBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  {nseBusy ? "Fetching..." : "Fetch from NSE"}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Max range: 200 days per call. Weekends are skipped automatically. NSE holidays may show as failures.
              </p>
              {nseResult && (
                <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-md p-4 font-mono text-xs space-y-1">
                  <div>
                    Days fetched: <span className="text-profit font-semibold">{nseResult.days_fetched}</span>{" · "}
                    Days failed: <span className={nseResult.days_failed > 0 ? "text-loss font-semibold" : "text-zinc-600"}>{nseResult.days_failed}</span>
                  </div>
                  {nseResult.failures?.length > 0 && (
                    <div className="text-zinc-600 mt-2 break-words">
                      Failed dates: {nseResult.failures.slice(0, 8).join(", ")}
                      {nseResult.failures.length > 8 && ` and ${nseResult.failures.length - 8} more`}
                    </div>
                  )}
                  <div className="text-zinc-500 mt-2">{nseResult.note}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold text-zinc-950">Bulk upload — Indices only</h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    For long-range backtests where you don't have stock-level bhavcopy data. CSV columns:{" "}
                    <span className="font-mono">trade_date, nifty50_close, nifty_smallcap50_close</span>.
                    Re-uploading the same date replaces the previous entry. Date can be{" "}
                    <span className="font-mono">2024-03-01</span>,{" "}
                    <span className="font-mono">01-Mar-2024</span>, or{" "}
                    <span className="font-mono">01/03/2024</span>.
                  </p>
                </div>
                <Badge className="bg-purple-50 text-purple-700 border-purple-200">Index-only</Badge>
              </div>
              <form onSubmit={uploadIndices} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <Label>Indices CSV file</Label>
                  <Input data-testid="idx-bulk-file" type="file" accept=".csv" required onChange={(e) => setIdxBulkFile(e.target.files[0])} className="mt-1" />
                </div>
                <Button data-testid="idx-bulk-btn" type="submit" disabled={idxBulkBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  {idxBulkBusy ? "Uploading..." : "Upload Indices"}
                </Button>
              </form>
              {idxBulkResult && (
                <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-md p-4 font-mono text-xs space-y-1">
                  <div>Rows inserted: <span className="text-zinc-900 font-semibold">{idxBulkResult.rows_inserted}</span></div>
                  {idxBulkResult.issues?.length > 0 && (
                    <div className="text-amber-700 mt-2">
                      Issues ({idxBulkResult.issues.length}): {idxBulkResult.issues.slice(0, 3).join(" · ")}
                      {idxBulkResult.issues.length > 3 && " …"}
                    </div>
                  )}
                </div>
              )}
              <details className="mt-4 text-xs text-zinc-600">
                <summary className="cursor-pointer text-zinc-700 font-semibold">Sample CSV format</summary>
                <pre className="mt-2 p-3 bg-zinc-50 border border-zinc-200 rounded overflow-x-auto text-[11px] leading-snug">{`trade_date,nifty50_close,nifty_smallcap50_close
2023-01-02,18197.45,9302.10
2023-01-03,18232.55,9341.85
2023-01-04,18042.95,9224.40
2023-01-05,17992.15,9180.50`}</pre>
              </details>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Chart Repair</div>
              <h3 className="font-display font-bold text-xl text-zinc-950 mt-2">Backfill portfolio snapshots</h3>
              <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
                Iterates every uploaded bhavcopy and writes/refreshes a daily snapshot for every active
                portfolio. Fixes gaps in the customer &quot;Portfolio vs Benchmarks&quot; chart on days when the
                recommendation engine didn&apos;t run. Idempotent — safe to click any time.
              </p>
              <Button
                data-testid="backfill-snapshots-btn"
                onClick={backfillSnapshots}
                disabled={snapBackfillBusy}
                className="mt-4 bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                {snapBackfillBusy ? "Backfilling…" : "Backfill snapshots from bhavcopies"}
              </Button>
              {snapBackfillResult && (
                <div className="mt-4 inline-block text-left bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-4 font-mono text-xs" data-testid="backfill-result">
                  <div>Days processed: {snapBackfillResult.days_processed}</div>
                  <div>Snapshots written: {snapBackfillResult.snapshots_written}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-0">
              <div className="px-6 py-4 border-b border-zinc-200 font-display font-semibold text-zinc-950">Upload history</div>
              <Table>
                <TableHeader><TableRow><TableHead>Trade Date</TableHead><TableHead>Filename</TableHead><TableHead className="text-right">Symbols</TableHead><TableHead className="text-right">Nifty 50</TableHead><TableHead className="text-right">Smallcap 50</TableHead></TableRow></TableHeader>
                <TableBody>
                  {bhav.map((b) => (
                    <TableRow key={b.bhav_id}>
                      <TableCell className="font-mono text-sm">{fmtDate(b.trade_date)}</TableCell>
                      <TableCell className="text-sm">{b.filename}</TableCell>
                      <TableCell className="text-right font-mono">{b.rows_count}</TableCell>
                      <TableCell className="text-right font-mono">{num(b.nifty50_close)}</TableCell>
                      <TableCell className="text-right font-mono">{num(b.nifty_smallcap50_close)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engine" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-8 text-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Recommendation Engine</div>
              <h3 className="font-display font-bold text-2xl text-zinc-950 mt-2">Run end-of-day batch</h3>
              <p className="text-sm text-zinc-500 max-w-md mx-auto mt-3">
                Processes pending recommendations across all active portfolios. Sells before buys.
                Caps at each strategy's configured Max Portfolio Size. Sell proceeds and unspent allocations sit as idle cash on the portfolio.
              </p>
              <Button data-testid="run-engine-btn" onClick={runEngine} className="mt-6 bg-zinc-950 hover:bg-zinc-800 text-white px-8">
                Run Engine Now
              </Button>
              {engineResult && (
                <div data-testid="engine-result-card" className="mt-6 inline-block text-left bg-zinc-50 border border-zinc-200 rounded-md p-4 font-mono text-xs w-full max-w-2xl">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div data-testid="engine-trade-date"><span className="text-zinc-400">Trade date</span><br />{engineResult.trade_date}</div>
                    <div data-testid="engine-portfolios"><span className="text-zinc-400">Portfolios</span><br />{engineResult.portfolios_processed}</div>
                    <div data-testid="engine-buys"><span className="text-zinc-400">Buys</span><br /><span className="text-profit">{engineResult.buys}</span></div>
                    <div data-testid="engine-sells"><span className="text-zinc-400">Sells</span><br /><span className="text-loss">{engineResult.sells}</span></div>
                    <div data-testid="engine-skipped"><span className="text-zinc-400">Skipped</span><br /><span className={engineResult.skipped > 0 ? "text-amber-600" : ""}>{engineResult.skipped ?? 0}</span></div>
                  </div>
                  {(engineResult.remarks?.length > 0 || engineResult.skipped > 0) && (
                    <div className="mt-3">
                      <button
                        data-testid="engine-details-toggle"
                        onClick={() => setEngineDetailsOpen((v) => !v)}
                        className="text-zinc-950 font-semibold underline underline-offset-2 hover:text-zinc-600 transition-colors"
                      >
                        {engineDetailsOpen ? "Hide details ▲" : "Show details ▼"}
                      </button>
                      {engineDetailsOpen && (
                        <div data-testid="engine-details-body" className="mt-2 space-y-1.5">
                          {engineResult.remarks?.length > 0 ? (
                            <ul data-testid="engine-remarks-list" className="list-disc pl-4 space-y-1 text-amber-700">
                              {engineResult.remarks.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          ) : (
                            <div className="text-zinc-500">No remarks from the engine.</div>
                          )}
                          <div className="text-zinc-400 pt-1 border-t border-zinc-200">
                            Run id: {engineResult._history_run_id || "n/a"} · Full log saved to Engine History.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="backtest" className="mt-4 space-y-4">
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Backtest</div>
              <h3 className="font-display font-bold text-2xl text-zinc-950 mt-2 mb-2">Replay engine over a historical window</h3>
              <p className="text-sm text-zinc-500 mb-6 max-w-2xl">
                Iterates every uploaded Bhavcopy in the selected range chronologically and runs the engine for that day,
                using that day's prices and Nifty50 / Smallcap50 closes. Recommendations are applied
                from their <span className="font-mono">Effective from</span> date. The customer's chart will reflect the
                full historical evolution.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>From date</Label>
                  <Input data-testid="backtest-from-input" type="date" required value={backForm.from_date} onChange={(e) => setBackForm({...backForm, from_date: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label>To date</Label>
                  <Input data-testid="backtest-to-input" type="date" required value={backForm.to_date} onChange={(e) => setBackForm({...backForm, to_date: e.target.value})} className="mt-1" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
                    <input
                      data-testid="backtest-reset-checkbox"
                      type="checkbox"
                      checked={backForm.reset}
                      onChange={(e) => setBackForm({...backForm, reset: e.target.checked})}
                      className="h-4 w-4 rounded border-zinc-300"
                    />
                    Reset before run
                  </label>
                </div>
                <div className="flex items-end">
                  <Button data-testid="run-backtest-btn" onClick={runBacktest} disabled={backBusy} className="bg-zinc-950 hover:bg-zinc-800 text-white w-full">
                    {backBusy ? "Running..." : "Run Backtest"}
                  </Button>
                </div>
              </div>
              <div className="text-xs text-zinc-500 mt-4 max-w-2xl">
                <span className="font-semibold text-zinc-700">Reset</span> wipes all holdings + snapshots for matching portfolios,
                resets <span className="font-mono">cash_balance</span> to each portfolio's <span className="font-mono">initial_value</span>,
                and re-marks all recommendations as pending. Use this to get a clean replay.
              </div>
              {backProgress && (
                <div className="mt-4 p-4 bg-zinc-50 border border-zinc-200 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Backtest progress</div>
                    <div className="text-xs font-mono text-zinc-500">{backProgress.status}</div>
                  </div>
                  {backProgress.days_total > 0 && (
                    <>
                      <div className="w-full bg-zinc-200 rounded-full h-2 overflow-hidden">
                        <div className="bg-zinc-950 h-2 transition-all" style={{ width: `${(backProgress.days_done / backProgress.days_total * 100).toFixed(1)}%` }} />
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 mt-2">
                        {backProgress.days_done} / {backProgress.days_total} days
                        {backProgress.current_day && <span className="ml-2">· current: {backProgress.current_day}</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {backResult && (
            <Card className="border-zinc-200 shadow-none">
              <CardContent className="p-0">
                <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                  <div className="font-display font-semibold text-zinc-950">Backtest Result</div>
                  <div className="font-mono text-xs text-zinc-600">
                    {backResult.from_date} → {backResult.to_date} · {backResult.trade_days} days · {backResult.total_buys} buys · {backResult.total_sells} sells
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trade Date</TableHead>
                      <TableHead>Portfolios</TableHead>
                      <TableHead className="text-right">Buys</TableHead>
                      <TableHead className="text-right">Sells</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backResult.days.map((d) => (
                      <TableRow key={d.trade_date}>
                        <TableCell className="font-mono text-sm">{fmtDate(d.trade_date)}</TableCell>
                        <TableCell className="font-mono">{d.portfolios_processed}</TableCell>
                        <TableCell className="text-right font-mono"><span className={d.buys > 0 ? "text-profit" : "text-zinc-400"}>{d.buys}</span></TableCell>
                        <TableCell className="text-right font-mono"><span className={d.sells > 0 ? "text-loss" : "text-zinc-400"}>{d.sells}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
